import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { getJurisdiction, type JurisdictionConfig } from '@/jurisdictions';
import { isUsRegion } from '@/jurisdictions/regions';
import type { RegionalDateLocale } from '@/utils/date-utils';

export type MatterLocale = RegionalDateLocale;

interface MatterJurisdictionContextValue {
  region: string;
  /**
   * The raw matter region, or `null` when there is no matter in scope (e.g.
   * account settings, analytics, OAuth callbacks). Unlike `region`, this is not
   * defaulted to `'UK'`, so consumers can fall back to a different source (such
   * as the deployment region) when no matter is present.
   */
  matterRegion: string | null;
  forum: string | null;
  locale: MatterLocale;
  jurisdiction: JurisdictionConfig;
}

function matterLocale(region?: string | null): MatterLocale {
  return isUsRegion(region) ? 'us' : 'uk';
}

function buildMatterJurisdiction(
  region?: string | null,
  forum?: string | null,
): MatterJurisdictionContextValue {
  const resolvedRegion = region || 'UK';
  const resolvedForum = forum ?? null;
  return {
    region: resolvedRegion,
    matterRegion: region || null,
    forum: resolvedForum,
    locale: matterLocale(resolvedRegion),
    jurisdiction: getJurisdiction(resolvedRegion, resolvedForum),
  };
}

const MatterJurisdictionContext = createContext<MatterJurisdictionContextValue>(
  buildMatterJurisdiction(),
);

export function MatterJurisdictionProvider({
  region,
  forum,
  children,
}: {
  region?: string | null;
  forum?: string | null;
  children: ReactNode;
}) {
  const value = useMemo(() => buildMatterJurisdiction(region, forum), [region, forum]);

  return (
    <MatterJurisdictionContext.Provider value={value}>
      {children}
    </MatterJurisdictionContext.Provider>
  );
}

export function useMatterJurisdiction(): MatterJurisdictionContextValue {
  return useContext(MatterJurisdictionContext);
}

export function useMatterLocale(): MatterLocale {
  return useMatterJurisdiction().locale;
}
